/**
 * 2D landing-page shared leaderboard (issue #36, Sfida Phase 3). A collapsible panel that
 * shows the public arcade board — rank · nickname · score (+ max combo / date) — with the
 * current player's row highlighted, plus an editor for the leaderboard nickname.
 *
 * The board read is public (no auth needed) but the Firestore SDK is heavy and lazy, so the
 * fetch is pulled via dynamic import only when the player opens the panel (a paid read). The
 * whole section stays hidden in local-only mode (Firebase unconfigured). Pure DOM — no
 * canvas, no VR.
 */
import { difficultyLabel, getLocale, t } from '../i18n';
import { isFirebaseConfigured } from '../firebase/config';
import { getEffectiveNickname, setNickname } from '../settings/nickname';
import {
  DIFFICULTY,
  DIFFICULTY_ORDER,
  LEADERBOARD_NICKNAME_MAX_LEN,
  TECHNIQUE,
  type Difficulty,
  type DifficultyPreset,
  type Technique,
} from '../config/constants';
import type { LeaderboardBoard, LeaderboardEntry } from '../firebase/leaderboard';

/** The preset difficulties that have a board (custom has none). */
const BOARD_DIFFICULTIES = DIFFICULTY_ORDER.filter(
  (d): d is DifficultyPreset => d !== DIFFICULTY.custom,
);

/** Wire the landing leaderboard panel. No-op if the container is absent or local-only. */
export function bootstrapLeaderboard(): void {
  const container = document.getElementById('leaderboard');
  if (!container) return;
  // Local-only mode: there is no shared board to show — leave the section absent.
  if (!isFirebaseConfigured()) return;

  const panel = document.createElement('details');
  panel.className = 'lb-panel';
  const summary = document.createElement('summary');
  summary.textContent = t('leaderboardTitle');
  panel.appendChild(summary);

  const body = document.createElement('div');
  body.className = 'lb-body';
  panel.appendChild(body);
  container.appendChild(panel);

  // Nickname editor is always available — set your name before your first run.
  body.appendChild(buildNicknameEditor());

  // Boards are separate per technique (#44) and per difficulty (#43) — two tab rows pick which.
  let technique: Technique = TECHNIQUE.cut;
  let difficulty: Difficulty = DIFFICULTY.medium;

  const board = document.createElement('div');
  board.className = 'lb-board';

  const tabs = document.createElement('div');
  tabs.className = 'lb-tabs';
  const tabCut = document.createElement('button');
  tabCut.type = 'button';
  tabCut.textContent = t('leaderboardTabCut');
  const tabThrust = document.createElement('button');
  tabThrust.type = 'button';
  tabThrust.textContent = t('leaderboardTabThrust');
  tabs.append(tabCut, tabThrust);

  // Difficulty tab row (the 4 preset boards; custom has none).
  const diffTabs = document.createElement('div');
  diffTabs.className = 'lb-tabs lb-tabs-difficulty';
  const diffButtons = new Map<DifficultyPreset, HTMLButtonElement>();
  for (const d of BOARD_DIFFICULTIES) {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.textContent = difficultyLabel(d);
    diffButtons.set(d, btn);
    diffTabs.appendChild(btn);
  }
  body.append(tabs, diffTabs, board);

  const refreshBtn = document.createElement('button');
  refreshBtn.type = 'button';
  refreshBtn.className = 'lb-refresh';
  refreshBtn.textContent = t('leaderboardRefresh');
  body.appendChild(refreshBtn);

  const syncTabs = (): void => {
    tabCut.className = technique === TECHNIQUE.cut ? 'lb-tab active' : 'lb-tab';
    tabThrust.className = technique === TECHNIQUE.thrust ? 'lb-tab active' : 'lb-tab';
    for (const [d, btn] of diffButtons) {
      btn.className = d === difficulty ? 'lb-tab active' : 'lb-tab';
    }
  };
  syncTabs();

  let loaded = false; // whether the board has been fetched at least once
  const refresh = async (): Promise<void> => {
    board.replaceChildren(messageEl(t('leaderboardLoading')));
    refreshBtn.disabled = true;
    try {
      const { fetchLeaderboardBoard } = await import('../firebase/leaderboard');
      const data = await fetchLeaderboardBoard(technique, difficulty);
      loaded = true;
      if (!data) board.replaceChildren(messageEl(t('leaderboardUnavailable')));
      else renderBoard(board, data);
    } catch (err) {
      console.warn('[leaderboard] fetch failed', err);
      board.replaceChildren(messageEl(t('leaderboardUnavailable')));
    } finally {
      refreshBtn.disabled = false;
    }
  };

  const selectTechnique = (next: Technique): void => {
    if (next === technique) return;
    technique = next;
    syncTabs();
    void refresh();
  };
  tabCut.addEventListener('click', () => selectTechnique(TECHNIQUE.cut));
  tabThrust.addEventListener('click', () => selectTechnique(TECHNIQUE.thrust));

  const selectDifficulty = (next: Difficulty): void => {
    if (next === difficulty) return;
    difficulty = next;
    syncTabs();
    void refresh();
  };
  for (const [d, btn] of diffButtons) btn.addEventListener('click', () => selectDifficulty(d));

  refreshBtn.addEventListener('click', () => void refresh());
  // Lazy: only read Firestore once the player actually opens the panel.
  panel.addEventListener('toggle', () => {
    if (panel.open && !loaded) void refresh();
  });

  // Anonymous sign-in is async and a Google link changes the uid — re-fetch once auth
  // settles, but only after the board has been shown (so we don't read on a closed panel).
  void import('../firebase/auth').then(({ onAuthChanged }) => {
    onAuthChanged(() => {
      if (loaded) void refresh();
    });
  });
}

// ── Board ────────────────────────────────────────────────────
function renderBoard(wrap: HTMLElement, board: LeaderboardBoard): void {
  if (board.rows.length === 0) {
    wrap.replaceChildren(messageEl(t('leaderboardEmpty')));
    return;
  }

  const head = document.createElement('div');
  head.className = 'lb-head';
  const players = document.createElement('span');
  players.textContent = t('leaderboardPlayers')(board.total);
  head.appendChild(players);
  if (board.myRank != null) {
    const mine = document.createElement('span');
    mine.className = 'lb-myrank';
    mine.textContent = t('leaderboardYourRank')(board.myRank);
    head.appendChild(mine);
  }

  const list = document.createElement('div');
  list.className = 'lb-rows';
  for (const row of board.rows) list.appendChild(buildRow(row));

  wrap.replaceChildren(head, list);
}

/** A single board row: rank · nickname (+ combo/date) · score, highlighted when it's you. */
function buildRow(row: LeaderboardEntry): HTMLElement {
  const el = document.createElement('div');
  el.className = row.isMe ? 'lb-row me' : 'lb-row';

  const rank = document.createElement('span');
  rank.className = 'lb-rank';
  rank.textContent = `#${row.rank}`;

  const main = document.createElement('div');
  main.className = 'lb-main';
  const name = document.createElement('span');
  name.className = 'lb-name';
  name.textContent = row.nickname;
  const meta = document.createElement('span');
  meta.className = 'lb-meta';
  meta.textContent = `${t('summaryMaxCombo')} ${row.comboMax} · ${formatDate(row.achievedAt)}`;
  main.append(name, meta);

  const score = document.createElement('span');
  score.className = 'lb-score';
  score.textContent = String(row.score);

  el.append(rank, main, score);
  return el;
}

// ── Nickname editor ──────────────────────────────────────────
/** Free-text leaderboard display name; persisted on change/blur (synced via UserSettings). */
function buildNicknameEditor(): HTMLElement {
  const box = document.createElement('div');
  box.className = 'lb-nickname';

  const label = document.createElement('label');
  label.className = 'lb-nickname-label';
  label.textContent = t('menuNickname');

  const input = document.createElement('input');
  input.type = 'text';
  input.maxLength = LEADERBOARD_NICKNAME_MAX_LEN;
  input.value = getEffectiveNickname();
  const commit = (): void => {
    setNickname(input.value);
    input.value = getEffectiveNickname(); // reflect trim/clamp + regenerated default if cleared
  };
  input.addEventListener('change', commit);
  input.addEventListener('blur', commit);
  label.appendChild(input);

  const hint = document.createElement('p');
  hint.className = 'lb-nickname-hint';
  hint.textContent = t('leaderboardNicknameHint');

  box.append(label, hint);
  return box;
}

// ── Helpers ──────────────────────────────────────────────────
function messageEl(text: string): HTMLElement {
  const p = document.createElement('p');
  p.className = 'lb-empty';
  p.textContent = text;
  return p;
}

function formatDate(ms: number): string {
  try {
    return new Intl.DateTimeFormat(getLocale(), { dateStyle: 'medium' }).format(new Date(ms));
  } catch {
    return new Date(ms).toISOString().slice(0, 10);
  }
}
